import React, { ReactNode, useEffect, useContext, useMemo, useRef, useCallback } from 'react'
import { routes } from 'routes'
import { isForbidden } from 'shared/hocs/withAuth'
import { matchPath } from 'react-router'
import pick from 'lodash/pick'
import upperFirst from 'lodash/upperFirst'
import { animated, useSpring } from 'react-spring'
import Box from 'shared/components/Box'
import { FORMSTACK_LINK } from 'shared/constants/variables'
import DefaultImage from 'assets/images/DefaultImage.png'
import ChallengeIcon from 'assets/images/icon_challenge.svg'
import ChallengeActiveIcon from 'assets/images/icon_challenge_active.png'
import NavigationList, { SubNavType } from 'shared/components/Layout/Sidebar/NavigationList'
import { useDispatch, useSelector } from 'react-redux'
import { createSelector } from '@reduxjs/toolkit'
import { GlobalState } from 'shared/redux/reducer'
import { getUserRoles, getUserInbox } from 'shared/redux/sidebar/reducer'
import { organizationInformationFeaturesReportNavType } from 'pages/CustomizeScoreboard/model/organizationInformation'
import { authSelector, permissionSelector, roleSelector, organizationSettingSelector } from 'shared/hocs/withAuth'
import { SVG_ICONS } from 'pages/UserRole/model/role'
import ProtectedImage from 'shared/components/ProtectedImage'
import styled, { ThemeContext } from 'styled-components'
import SidebarProfile from './SidebarProfile'
import Button from 'shared/components/Button'
import { RouteRoleLevel } from 'shared/types'
import SvgHomeIcon from 'assets/icons/Home'
import SvgCommunicationsIcon from 'assets/icons/Communications'
import SvgProgramsIcon from 'assets/icons/Programs'
import SvgProgramBuilderIcon from 'assets/icons/ProgramBuilder'
import SvgCheckInsIcon from 'assets/icons/Checkins'
import SvgScheduleIcon from 'assets/icons/Schedule'
import SvgStoreIcon from 'assets/icons/Store'
import SvgSurveysIcon from 'assets/icons/Surveys'
import SvgShareFeedbackIcon from 'assets/icons/ShareFeedback'
import SvgReportsIcon from 'assets/icons/Reports'
import SvgParticipantsIcon from 'assets/icons/Participants'
import SvgEventsIcon from 'assets/icons/Events'
import SvgPointsIcon from 'assets/icons/Points'
import SvgCustomizeIcon from 'assets/icons/Customize'
import SvgUserRoleIcon from 'assets/icons/UserRole'
import SvgWaiverIcon from 'assets/icons/Waiver'
import SvgResourcesIcon from 'assets/icons/Resources'
import SvgInboxIcon from 'assets/icons/Inbox'
import SvgOrganizationIcon from 'assets/icons/organizationIcon'
import SvgCoupleIcon from 'assets/icons/Couple'
import SvgFlagIcon from 'assets/icons/Flag'
import { showDialog, hideDialog } from 'shared/redux/app/reducer'
import SupportDialog from 'pages/Support/components/Dialogs/SupportDialog'
import SelectCheckInOutDialog from 'pages/CheckIn/components/Dialogs/SelectCheckInOut'
import CloseIcon from 'assets/icons/close.svg'
import {
  CAN_VIEW_PARTICIPANT_LIST,
  CAN_VIEW_CHECK_IN_OUT_MEMBERS,
  CAN_CREATE_POINTS,
  CAN_CREATE_SURVEY,
  CAN_EXPORT_SURVEY_RESULTS,
  CAN_VIEW_REPORTS,
  CAN_CREATE_COMMUNICATIONS,
  CAN_VIEW_SCHEDULE,
  CAN_VIEW_PROGRAMS,
  CAN_VIEW_EVENTS,
  CAN_VIEW_INBOX,
  CAN_VIEW_CHALLENGES,
  CAN_VIEW_STORE,
  CAN_MANAGE_PARENT,
} from 'shared/constants/permission'
import {
  FEATURE_VIEW_REPORTS,
  FEATURE_ENROLLMENT_PROGRAM,
  FEATURE_ENROLLMENT_EVENT,
  FEATURE_PROGRAM_BUILDER,
} from 'shared/constants/features'
import {
  COACH,
  ADMIN,
  ORGANIZATION,
  PARTICIPANT,
  PARENT,
  GRANTER,
  SUPER_ADMIN,
  PARTICIPANT_ROLE_TEXT,
  PARENT_ROLE_TEXT,
} from 'shared/hocs/withAuth'
import { useAtBreakpoints } from 'shared/hooks/useBreakpoint'
import { useMenu } from '../MenuContext'
import dayjs from 'dayjs'
import upperCase from 'lodash/upperCase'
import useClickOutside from 'shared/hooks/useClickOutside'

interface NavigationListItemType {
  to: string
  label: string | ReactNode
  icon: string | ReactNode
  iconActive: string | ReactNode
  level: RouteRoleLevel
  subnav: Array<SubNavType>
  requiredRoles?: string[]
  permissions: string[]
  isKioskAllowed?: boolean
  features?: string[]
}

export const sidebarSelector = createSelector(
  (state: GlobalState) => state.sidebar.sidebar_roles,
  (state: GlobalState) => state.sidebar.inbox,
  (sidebar_roles, inbox_count) => ({ sidebar_roles, inbox_count }),
)

const Sidebar: React.FC = () => {
  const theme = useContext(ThemeContext)
  const { sidebar_roles, inbox_count } = useSelector(sidebarSelector)
  const { auth: user } = useSelector(authSelector)
  const { organization_information } = useSelector(organizationSettingSelector)
  const { isParent, isParticipant, isKiosk } = useSelector(roleSelector)
  const {
    CAN_VIEW_COACH_LIST,
    CAN_CHECK_IN_OUT_MEMBERS: CAN_CHECK_IN_OUT_MEMBERS_PERMISSION,
    CAN_VIEW_CHECK_IN_OUT_MEMBERS: CAN_VIEW_CHECK_IN_OUT_MEMBERS_PERMISSION,
  } = useSelector(permissionSelector)
  const isLarge = useAtBreakpoints(['tablet-ipad-landscape', 'desktop'])
  const menu = useMenu()
  const sidebarRef = useRef(null)

  const roleLevel = useMemo(() => user?.role_level, [user])

  const hasCheckInOut = useMemo(
    () =>
      user &&
      user?.role_level > PARTICIPANT &&
      CAN_CHECK_IN_OUT_MEMBERS_PERMISSION &&
      organization_information.is_member_check_in_or_out &&
      !isKiosk,
    [user, organization_information.is_member_check_in_or_out, CAN_CHECK_IN_OUT_MEMBERS_PERMISSION, isKiosk],
  )

  const canViewCheckIn = useMemo(
    () =>
      user &&
      user?.role_level > PARTICIPANT &&
      CAN_VIEW_CHECK_IN_OUT_MEMBERS_PERMISSION &&
      organization_information.is_member_check_in_or_out,
    [user, organization_information.is_member_check_in_or_out, CAN_VIEW_CHECK_IN_OUT_MEMBERS_PERMISSION],
  )

  useClickOutside(sidebarRef, () => {
    if (menu.menuActive) {
      menu.hide()
    }
  })
  const dispatch = useDispatch()

  useEffect(() => {
    !sidebar_roles && dispatch(getUserRoles())
  }, [sidebar_roles])

  useEffect(() => {
    if ((roleLevel ?? 0) !== SUPER_ADMIN && (roleLevel ?? 0) !== GRANTER)
      inbox_count === null && dispatch(getUserInbox())
  }, [inbox_count, roleLevel])

  const userSubNav: Array<SubNavType> = useMemo(() => {
    const formatNav: any = (sidebar_roles || [])?.map((role: any) => {
      if (upperCase(role.name) === PARTICIPANT_ROLE_TEXT || upperCase(role.name) === PARENT_ROLE_TEXT) return

      const icon = SVG_ICONS.find(({ name }) => name === role?.icon)

      const subnav: SubNavType = {
        to: `/user/${role?.id}`,
        label: upperFirst((role?.display_name || role?.name) ?? ''),
        icon: <SvgParticipantsIcon />,
        iconActive: <SvgParticipantsIcon fill={theme.colors.main} />,
        level: null,
        permissions: [],
        exact: true,
      }

      if (icon?.Icon) {
        subnav.icon = <icon.Icon />
        subnav.iconActive = <icon.Icon fill={theme.colors.main} />
      }

      if (role?.level === COACH && role?.name?.includes('Coach') && !CAN_VIEW_COACH_LIST) return

      return subnav
    })

    return formatNav?.filter((each: any) => each)
  }, [sidebar_roles])

  const participantLabel = useMemo(() => {
    let label = 'Participants'
    if (sidebar_roles) {
      let participantRole = sidebar_roles.find(r => upperCase(r.name) === PARTICIPANT_ROLE_TEXT)
      if (participantRole) {
        label = participantRole.display_name || participantRole.name
      }
    }
    return label
  }, [sidebar_roles])

  const parentLabel = useMemo(() => {
    let label = 'Parents'
    if (sidebar_roles) {
      let parentRole = sidebar_roles.find(r => upperCase(r.name) === PARENT_ROLE_TEXT)
      if (parentRole) {
        label = parentRole.display_name || parentRole.name
      }
    }
    return label
  }, [sidebar_roles])

  const PARTICIPANT_MENU: NavigationListItemType = (() => {
    const data: NavigationListItemType = {
      to: '/participants',
      label: 'USERS',
      icon: <SvgParticipantsIcon />,
      iconActive: <SvgParticipantsIcon fill={theme.colors.main} />,
      level: null,
      permissions: [],
      subnav: [
        {
          to: '/user/flagged',
          label: 'Flagged Members',
          icon: <SvgFlagIcon />,
          iconActive: <SvgFlagIcon fill={theme.colors.main} />,
          level: null,
          permissions: [CAN_VIEW_PARTICIPANT_LIST],
          exact: true,
        },
        ...(isParent
          ? []
          : [
              {
                to: '',
                label: participantLabel,
                icon: <SvgParticipantsIcon />,
                iconActive: <SvgParticipantsIcon fill={theme.colors.main} />,
                level: null,
                permissions: [CAN_VIEW_PARTICIPANT_LIST],
              },
            ]),
        ...(isParticipant
          ? []
          : [
              {
                to: '/user/parent',
                label: parentLabel,
                icon: <SvgCoupleIcon />,
                iconActive: <SvgCoupleIcon fill={theme.colors.main} />,
                level: null,
                permissions: [CAN_MANAGE_PARENT],
                exact: true,
              },
            ]),
        ...(canViewCheckIn
          ? [
              {
                to: '/check-in',
                label: 'Check Ins',
                icon: <SvgCheckInsIcon />,
                iconActive: <SvgCheckInsIcon fill={theme.colors.main} />,
                level: null,
                permissions: [CAN_VIEW_CHECK_IN_OUT_MEMBERS],
                exact: true,
              },
            ]
          : []),
        {
          to: '/organization',
          label: 'Organizations',
          icon: <SvgOrganizationIcon />,
          iconActive: <SvgOrganizationIcon fill={theme.colors.main} />,
          level: {
            up: null,
            equal: [SUPER_ADMIN, GRANTER],
            down: null,
          },
          permissions: [],
          exact: true,
        },
        {
          to: '/granters',
          label: 'Granters',
          icon: <SvgParticipantsIcon />,
          iconActive: <SvgParticipantsIcon fill={theme.colors.main} />,
          level: {
            up: SUPER_ADMIN,
            equal: [SUPER_ADMIN],
            down: null,
          },
          permissions: [],
          exact: true,
        },

        ...userSubNav,
      ],
    }

    return data
  })()

  const HOME: NavigationListItemType = {
    to: '/',
    label: 'Home',
    icon: <SvgHomeIcon />,
    iconActive: <SvgHomeIcon fill={theme.colors.main} />,
    isKioskAllowed: true,
    level: {
      up: null,
      equal: [ORGANIZATION],
      down: ORGANIZATION,
    },
    permissions: [],
    subnav: [],
  }

  const PROGRAM: NavigationListItemType = {
    to: '/programs-events',
    label: 'PROGRAMS & EVENTS',
    icon: <SvgProgramsIcon />,
    iconActive: <SvgProgramsIcon fill={theme.colors.main} />,
    level: null,
    permissions: [],
    subnav: [
      {
        to: '',
        label: 'Schedule',
        icon: <SvgScheduleIcon />,
        isKioskAllowed: true,
        iconActive: <SvgScheduleIcon fill={theme.colors.main} />,
        level: null,
        permissions: [CAN_VIEW_SCHEDULE],
      },
      ...(organization_information.features.program_builder.is_enabled
        ? [
            {
              to: '/program-builder',
              label: 'Program Builder',
              icon: <SvgProgramBuilderIcon />,
              iconActive: <SvgProgramBuilderIcon fill={theme.colors.main} />,
              level: {
                up: ADMIN,
                equal: [ADMIN, ORGANIZATION],
                down: ORGANIZATION,
              },
              permissions: [],
              features: [FEATURE_PROGRAM_BUILDER],
            },
          ]
        : []),

      ...(user &&
      organization_information.features.program.is_enabled &&
      dayjs().isBefore(organization_information.features.program.program_cycle_end) &&
      (isParent || (isParticipant && (user.parent == null || (user.parent && user.is_enrollment_allowed))))
        ? [
            {
              to: '/program-registration',
              label: 'Program Registration',
              icon: <SvgProgramsIcon />,
              iconActive: <SvgProgramsIcon fill={theme.colors.main} />,
              level: {
                up: PARENT,
                equal: [PARTICIPANT, PARENT],
                down: PARTICIPANT,
              },
              permissions: [],
              features: [FEATURE_ENROLLMENT_PROGRAM],
            },
          ]
        : [
            {
              to: '/programs',
              label: 'Programs',
              icon: <SvgProgramsIcon />,
              iconActive: <SvgProgramsIcon fill={theme.colors.main} />,
              level: null,
              permissions: [CAN_VIEW_PROGRAMS],
            },
          ]),
      ...(user &&
      organization_information.features.event.is_enabled &&
      (isParent || (isParticipant && (user.parent == null || (user.parent && user.is_enrollment_allowed))))
        ? [
            {
              to: '/event-registration',
              label: 'Event Registration',
              icon: <SvgEventsIcon />,
              iconActive: <SvgEventsIcon fill={theme.colors.main} />,
              level: {
                up: PARENT,
                equal: [PARTICIPANT, PARENT],
                down: PARTICIPANT,
              },
              permissions: [],
              features: [FEATURE_ENROLLMENT_EVENT],
            },
          ]
        : [
            {
              to: '/events',
              label: 'Events',
              icon: <SvgEventsIcon />,
              iconActive: <SvgEventsIcon fill={theme.colors.main} />,
              level: null,
              permissions: [CAN_VIEW_EVENTS],
            },
          ]),
    ],
  }

  const REWARD: NavigationListItemType = {
    to: '/points-rewards',
    label: 'POINTS & REWARDS',
    icon: <SvgPointsIcon />,
    iconActive: <SvgPointsIcon fill={theme.colors.main} />,
    level: null,
    permissions: [],
    subnav: [
      {
        to: '',
        label: 'Points',
        icon: <SvgPointsIcon />,
        iconActive: <SvgPointsIcon fill={theme.colors.main} />,
        level: {
          up: COACH,
          equal: [ORGANIZATION, ADMIN, COACH],
          down: PARTICIPANT,
        },
        permissions: [CAN_CREATE_POINTS],
      },
      {
        to: '/store',
        label: 'Store',
        icon: <SvgStoreIcon />,
        iconActive: <SvgStoreIcon fill={theme.colors.main} />,
        level: {
          up: COACH,
          equal: COACH,
          down: null,
        },
        permissions: [CAN_VIEW_STORE],
        exact: true,
      },
      {
        to: '/survey',
        label: 'Surveys',
        icon: <SvgSurveysIcon />,
        iconActive: <SvgSurveysIcon fill={theme.colors.main} />,
        level: null,
        permissions: [CAN_CREATE_SURVEY, CAN_EXPORT_SURVEY_RESULTS],
        exact: true,
      },
    ],
  }

  const COMMUNICATION: NavigationListItemType = {
    to: '/communications',
    label: 'Communications',
    icon: <SvgCommunicationsIcon />,
    iconActive: <SvgCommunicationsIcon fill={theme.colors.main} />,
    level: {
      up: null,
      equal: [ORGANIZATION],
      down: ORGANIZATION,
    },
    permissions: [CAN_CREATE_COMMUNICATIONS],
    subnav: [],
  }

  const InboxLabel = useMemo(() => {
    let count: null | string | number = inbox_count || 0

    if (!count) return 'Inbox'

    if (count > 9) count = `9+`

    return (
      <InboxBadgeContainer>
        Inbox <InboxBadge>{`${count}`}</InboxBadge>
      </InboxBadgeContainer>
    )
  }, [inbox_count])

  const INBOX: NavigationListItemType = {
    to: '/inbox',
    label: InboxLabel,
    icon: <SvgInboxIcon />,
    iconActive: <SvgInboxIcon fill={theme.colors.main} />,
    level: null,
    permissions: [CAN_VIEW_INBOX],
    subnav: [],
  }

  const REPORTS_MENU: NavigationListItemType = (() => {
    const data: NavigationListItemType = {
      to: '/reports',
      label: 'Reports',
      icon: <SvgReportsIcon />,
      iconActive: <SvgReportsIcon fill={theme.colors.main} />,
      level: {
        up: ADMIN,
        equal: [ADMIN, ORGANIZATION],
        down: ORGANIZATION,
      },
      permissions: [CAN_VIEW_REPORTS],
      features: [FEATURE_VIEW_REPORTS],
      subnav: [],
    }

    if (organization_information.features.report) {
      organization_information.features.report.nav.map((navItem: organizationInformationFeaturesReportNavType) => {
        if (navItem.title && navItem.tableau_link) {
          data.subnav.push({
            to: `/${encodeURI(navItem.title.replace(/[^A-Z0-9]+/gi, '_').toLowerCase())}`,
            label: `${navItem.title}`,
            icon: <SvgReportsIcon />,
            iconActive: <SvgReportsIcon fill={theme.colors.main} />,
            level: {
              up: ADMIN,
              equal: [ADMIN, ORGANIZATION],
              down: ORGANIZATION,
            },
            permissions: [CAN_VIEW_REPORTS],
            features: [FEATURE_VIEW_REPORTS],
          })
        }
      })
    }

    return data
  })()

  const CHALLENGE: NavigationListItemType = {
    to: '/challenges',
    label: 'CHALLENGES',
    icon: ChallengeIcon,
    iconActive: ChallengeActiveIcon,
    level: {
      up: null,
      equal: PARTICIPANT,
      down: PARTICIPANT,
    },
    permissions: [],
    subnav: [
      {
        to: '',
        label: 'Surveys',
        icon: <SvgSurveysIcon />,
        iconActive: <SvgSurveysIcon fill={theme.colors.main} />,
        level: {
          up: null,
          equal: PARTICIPANT,
          down: PARTICIPANT,
        },
        permissions: [CAN_VIEW_CHALLENGES],
      },
      {
        to: '/store',
        label: 'Store',
        icon: <SvgStoreIcon />,
        iconActive: <SvgStoreIcon fill={theme.colors.main} />,
        level: {
          up: null,
          equal: PARTICIPANT,
          down: PARTICIPANT,
        },
        permissions: [CAN_VIEW_STORE],
        exact: true,
      },
    ],
  }

  const HELP: NavigationListItemType = {
    to: '',
    label: 'HELP',
    icon: ChallengeIcon,
    iconActive: ChallengeActiveIcon,
    level: null,
    permissions: [],
    subnav: [
      {
        isButton: true,
        onButtonClick: () => {
          if (organization_information.support_email) {
            dispatch(
              showDialog({
                component: SupportDialog,
                props: {
                  icon: null,
                  title: `Scoreboard Support`,
                  supportSubject: `Scoreboard Support`,
                  hasCancel: false,
                  onValid: () => {
                    dispatch(hideDialog())
                  },
                },
              }),
            )
          } else {
            window.open(FORMSTACK_LINK, '_blank', 'noopener,noreferrer')
          }
        },
        to: '/help',
        icon: <SvgShareFeedbackIcon />,
        iconActive: <SvgShareFeedbackIcon fill={theme.colors.main} />,
        level: null,
        permissions: [],
        label: 'Get In Touch',
      },
    ],
  }

  const ORGANIZATION_MENU: NavigationListItemType = {
    to: '/resource',
    label: 'ORGANIZATION',
    icon: <SvgParticipantsIcon />,
    iconActive: <SvgParticipantsIcon fill={theme.colors.main} />,
    level: null,
    permissions: [],
    subnav: [
      {
        to: '',
        label: 'Resources',
        icon: <SvgResourcesIcon />,
        iconActive: <SvgResourcesIcon fill={theme.colors.main} />,
        level: null,
        permissions: [],
      },
      {
        to: '/waivers',
        label: 'Waivers',
        icon: <SvgWaiverIcon />,
        iconActive: <SvgWaiverIcon fill={theme.colors.main} />,
        level: null,
        permissions: [],
        exact: true,
      },
      {
        to: '/role',
        label: 'User Groups',
        icon: <SvgUserRoleIcon />,
        iconActive: <SvgUserRoleIcon fill={theme.colors.main} />,
        level: null,
        permissions: [],
        exact: true,
      },
      {
        to: '/customize',
        label: 'Customize Scoreboard',
        icon: <SvgCustomizeIcon />,
        iconActive: <SvgCustomizeIcon fill={theme.colors.main} />,
        level: null,
        permissions: [],
        exact: true,
      },
    ],
  }

  const ORGANIZATION_REQUEST: NavigationListItemType = {
    to: '',
    label: 'REQUESTS',
    icon: <SvgParticipantsIcon />,
    iconActive: <SvgParticipantsIcon fill={theme.colors.main} />,
    level: {
      up: SUPER_ADMIN,
      equal: [SUPER_ADMIN],
      down: null,
    },
    permissions: [],
    subnav: [
      {
        to: '/requests/organization',
        label: 'Organization Requests',
        icon: <SvgCustomizeIcon />,
        iconActive: <SvgCustomizeIcon fill={theme.colors.main} />,
        level: {
          up: SUPER_ADMIN,
          equal: [SUPER_ADMIN],
          down: null,
        },
        permissions: [],
        exact: true,
      },
    ],
  }

  const MENU_ITEMS = useMemo(() => {
    const items = [
      HOME,
      INBOX,
      COMMUNICATION,
      ORGANIZATION_MENU,
      ORGANIZATION_REQUEST,
      PARTICIPANT_MENU,
      PROGRAM,
      REWARD,
      CHALLENGE,
      REPORTS_MENU,
      HELP,
    ]

    function filterNav(parentPath = '', navs: Array<SubNavType>): Array<SubNavType> {
      return navs.reduce((filteredNavs: Array<SubNavType>, nav: SubNavType) => {
        const match = routes.find((route: any) =>
          matchPath(nav?.exact ? nav?.to : `${parentPath}${nav?.to}`, pick(route, ['path', 'exact', 'strict'])),
        )

        if (!match) return filteredNavs
        const { level, permissions, features, isKioskAllowed = false } = match.pageProps

        if (isForbidden(user, organization_information, level, permissions, features, isKioskAllowed))
          return filteredNavs

        if (
          (nav?.permissions || []).length &&
          isForbidden(user, organization_information, nav?.level, nav?.permissions, features, nav.isKioskAllowed)
        )
          return filteredNavs

        if (
          (nav?.features || []).length &&
          isForbidden(user, organization_information, nav?.level, nav?.permissions, nav.features, nav.isKioskAllowed)
        )
          return filteredNavs

        if (
          !nav?.isKioskAllowed &&
          isForbidden(user, organization_information, nav?.level, nav?.permissions, nav?.features, nav.isKioskAllowed)
        )
          return filteredNavs

        return filteredNavs.concat(nav)
      }, [])
    }

    return items.reduce((RESULT: Array<NavigationListItemType>, NAV: NavigationListItemType) => {
      const { to = '', subnav = [] } = NAV

      if (NAV?.label === 'USERS' && isParent) return RESULT
      const found: any = routes.find((route: any) => matchPath(to, pick(route, ['path', 'exact', 'strict'])))
      if (
        !subnav.length &&
        isForbidden(user, organization_information, NAV.level, NAV?.permissions, NAV?.features, NAV?.isKioskAllowed)
      )
        return RESULT

      const { level, permissions, features, isKioskAllowed = false } = found?.pageProps
      const filteredSubNav = filterNav(to, subnav)
      if (
        isForbidden(
          user,
          organization_information,
          NAV?.level || level,
          permissions,
          features,
          NAV?.isKioskAllowed || isKioskAllowed,
        ) &&
        !filteredSubNav.length
      )
        return RESULT

      if (subnav.length && !filteredSubNav.length) return RESULT
      return RESULT.concat({ ...NAV, subnav: filteredSubNav })
    }, [])
  }, [
    HOME,
    INBOX,
    COMMUNICATION,
    ORGANIZATION,
    PARTICIPANT_MENU,
    PROGRAM,
    REWARD,
    CHALLENGE,
    REPORTS_MENU,
    HELP,
    inbox_count,
    InboxLabel,
    sidebar_roles,
    userSubNav,
    isParent,
    isParticipant,
    user,
    routes,
    organization_information,
  ]) as Array<NavigationListItemType>

  const navigation = (
    // <List>
    <List $hasCheckInOut={hasCheckInOut}>
      {MENU_ITEMS.map((nav, index) => (
        <NavigationList key={`navigation-${index}`} {...nav} />
      ))}
    </List>
  )

  const { translateX } = useSpring({
    translateX: menu.menuActive || isLarge ? 0 : -100,
  })

  const onCheckInOutClick = useCallback(() => {
    menu.hide()

    dispatch(
      showDialog({
        component: SelectCheckInOutDialog,
        props: {
          title: 'What Would You Like To Do?',
          dialogClassName: 'dialog--medium-1',
        },
      }),
    )
  }, [menu])

  return (
    <SidebarContainer $isLarge={isLarge}>
      {menu.menuActive && !isLarge && <Overlay />}
      <Content ref={sidebarRef} style={{ transform: translateX.interpolate((x: number) => `translate(${x}%)`) }}>
        <Header>
          <LogoTitle>
            <ProtectedImage
              src={organization_information.logo_left_sidebar_url ? organization_information.logo_left_sidebar_url : ''}
              alt={
                organization_information.logo_left_sidebar_alt_text
                  ? organization_information.logo_left_sidebar_alt_text
                  : `${organization_information.name} Logo`
              }
              defaultSrc={DefaultImage}
              className="logo"
            />
            <span>{organization_information.name}</span>
          </LogoTitle>
          {!isLarge && <CloseIconImg src={CloseIcon} onClick={menu.hide} />}
        </Header>
        <SidebarProfile />
        {navigation}
        {hasCheckInOut && <CheckIn onClick={onCheckInOutClick}>Check Users In/Out</CheckIn>}
      </Content>
    </SidebarContainer>
  )
}

const SidebarContainer = styled(animated.div)<{ $isLarge: boolean }>`
  ${({ $isLarge }) =>
    $isLarge
      ? `width: 320px;`
      : `
      position: fixed;
      z-index: 16;
    `}
`

const Overlay = styled.div`
  width: 100%;
  height: 100%;
  position: fixed;
  background: rgba(0, 0, 0, 0.7);
`

const Content = styled(animated.div)`
  height: 100vh;
  width: 320px;
  box-sizing: border-box;
  position: fixed;
  overflow: hidden;
  z-index: 1;
  letter-spacing: 0.33px;
  background: #ffffff;

  ${({ theme }) => theme.mediaQueries.large} {
    background: #f9f9f9;
  }
`

const Header = styled.div`
  padding: 32px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .icon-menu {
    display: flex;
    align-items: center;
    height: 24px;
    width: 24px;

    &:hover {
      cursor: pointer;
    }

    img {
      max-width: 100%;
    }
  }
`

const CloseIconImg = styled.img`
  cursor: pointer;
`

const LogoTitle = styled.div`
  height: 46px;
  display: flex;
  align-items: center;
  opacity: 1;
  transition: opacity 0.2s ease-in-out;
  color: ${({ theme }) => theme.colors.secondary};

  .logo {
    height: 46px;
    vertical-align: middle;
    margin-right: 16px;
  }

  span {
    font-size: 16px;
    font-weight: bold;
    width: 170px;
    line-height: 19px;
  }
`

const List = styled.div<{ $hasCheckInOut: boolean | null }>`
  list-style: none;
  padding: 0 20px;
  overflow-y: auto;
  margin-right: 10px;

  ${({ $hasCheckInOut }) => `
    height:  ${$hasCheckInOut ? `calc(100vh - 320px)` : `calc(100vh - 230px)`};
  `}

  &::-webkit-scrollbar {
    width: 8px;
    background-color: #f5f5f5;
    border-radius: 5px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.label};
    border-radius: 50px;
  }
`

const CheckIn = styled(Button)`
  position: fixed;
  bottom: 0;
  z-index: 1;
  width: 320px;
  height: 60px;
  border-radius: 0;
  background: ${({ theme }) => theme.colors.main};
  font-weight: bold;
  font-size: 18px;
  color: ${({ theme }) => theme.colors.white};
`
const InboxBadge = styled(Box)`
  width: 24px;
  height: 24px;
  background: ${({ theme }) => theme.colors.main};
  border-radius: 100%;
  margin-left: 21px;

  font-weight: bold;
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.colors.white};

  display: flex;
  align-items: center;
  justify-content: center;
`

const InboxBadgeContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
`

export default Sidebar
